import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./RegistrationPage.css";
import Loader from "../../Common/loader/Loader";
import {
  FaIdCard,
  FaGlobe,
  FaBuilding,
  FaArrowRight,
  FaCircleCheck,
  FaTicket,
  FaFire,
  FaShieldHalved,
} from "react-icons/fa6";
import { useFormContext } from "../../context/Form_context";
import { renderTitleWithGradient } from "../../utils/helper";

const getCategoryMeta = (cat) => {
  const name = String(cat?.name || "").toLowerCase();

  if (name.includes("international")) {
    return {
      icon: <FaGlobe />,
      path: "/internationalvisitors",
      themeClass: "reg-card-international",
    };
  }
  if (name.includes("exhibit")) {
    return {
      icon: <FaBuilding />,
      path: "/exhibitors",
      themeClass: "reg-card-exhibitors",
    };
  }
  return {
    icon: <FaIdCard />,
    path: "/domesticvisitors",
    themeClass: "reg-card-domestic",
  };
};

const parseHighlights = (detailHtml) => {
  if (!detailHtml) return [];

  return detailHtml
    .split(/<br\s*\/?>/i)
    .map((chunk) =>
      chunk
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, "")
        .trim(),
    )
    .filter((text) => text.length > 0);
};

const RegistrationPage = () => {
  const navigate = useNavigate();
  const { GetRegisterForm, register_form_data, register_form_loading } =
    useFormContext();

  useEffect(() => {
    GetRegisterForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    register_form_loading &&
    (!register_form_data || Object.keys(register_form_data).length === 0)
  ) {
    return <Loader fullScreen={true} text="Loading Registration Hub..." />;
  }

  return (
    <div className="reg-hub-page">
      <Helmet>
        <title>Registration | Bharat Buildcon</title>
      </Helmet>
      {/* Decorative Glow Orbs */}
      <div className="reg-bg-glow reg-bg-glow--saffron" />
      <div className="reg-bg-glow reg-bg-glow--blue" />

      {/* HERO BANNER & BREADCRUMB */}
      <section className="reg-hero-section">
        <div className="reg-container">
          <div className="clean-breadcrumb-wrapper">
            <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="bc-home-link">
                HOME
              </Link>
              <span className="bc-slash">/</span>
              <span className="bc-active-page">REGISTRATION</span>
            </nav>
          </div>

          <div className="reg-hero-header">
            <h1 className="reg-hero-title">
              {renderTitleWithGradient("Registration Hub")}
            </h1>

            {register_form_data?.registration_description && (
              <p className="reg-hero-subtitle">
                {register_form_data.registration_description}
              </p>
            )}
          </div>

          {/* PRICING & EARLY BIRD DISCOUNT BANNER */}
          {(register_form_data?.registration_visitor_title ||
            register_form_data?.registration_visitor_description) && (
            <div className="reg-css-reveal">
              <div className="reg-pricing-banner">
                {register_form_data?.registration_visitor_title && (
                  <div className="reg-banner-ribbon">
                    <FaFire className="reg-fire-icon" />
                    <span>{register_form_data.registration_visitor_title}</span>
                  </div>
                )}

                {register_form_data?.registration_visitor_description && (
                  <div className="reg-pricing-content">
                    <div className="reg-pricing-main">
                      <h2 className="reg-pricing-title">
                        {register_form_data.registration_visitor_description}
                      </h2>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CATEGORY SELECTION CARDS SECTION */}
      <section className="reg-main-section">
        <div className="reg-container">
          <div className="reg-css-reveal">
            <div className="reg-section-header">
              <h2 className="reg-sec-title">
                {renderTitleWithGradient("Registration Form Categories")}
              </h2>
              <p className="reg-sec-subtitle">
                Click your desired category below to complete your registration
                form:
              </p>
            </div>
          </div>

          {Array.isArray(register_form_data?.registration_categories) &&
            register_form_data.registration_categories.length > 0 && (
              <div className="reg-cards-grid">
                {register_form_data.registration_categories.map((cat, idx) => {
                  const meta = getCategoryMeta(cat);
                  const highlights = parseHighlights(cat.detail);
                  const badge = cat.tag?.toUpperCase();
                  const isExhibitor =
                    /exhibit/i.test(cat?.name || "") ||
                    /exhibit/i.test(cat?.sub_name || "") ||
                    /exhibit/i.test(cat?.tag || "") ||
                    meta.path === "/exhibitors";

                  return (
                    <div
                      key={cat.id || idx}
                      className="reg-card-slot reg-css-reveal"
                      style={{ animationDelay: `${idx * 120}ms` }}
                    >
                      <div className={`reg-category-card ${meta.themeClass}`}>
                        {badge && <div className="reg-card-badge">{badge}</div>}
                        {meta.icon && (
                          <div className="reg-card-icon-box">{meta.icon}</div>
                        )}

                        {cat.name && (
                          <h3 className="reg-card-title">{cat.name}</h3>
                        )}
                        {cat.sub_name && (
                          <span className="reg-card-subtitle">{cat.sub_name}</span>
                        )}
                        {cat.description && (
                          <p className="reg-card-desc">{cat.description}</p>
                        )}

                        {highlights.length > 0 && (
                          <div className="reg-highlights-list">
                            {highlights.map((item, i) => (
                              <div key={i} className="reg-hl-item">
                                <FaCircleCheck className="reg-hl-icon" />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <button
                          onClick={() =>
                            navigate(meta.path, {
                              state: { id: cat.id, categoryId: cat.id },
                            })
                          }
                          className="reg-btn-action"
                        >
                          <span>{isExhibitor ? "BOOK NOW" : "REGISTER NOW"}</span>
                          <FaArrowRight className="reg-btn-arrow" />
                        </button>

                        <div className="reg-card-bottom-accent" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </section>

      {/* SECURITY & SUPPORT BAR */}
      <section className="reg-trust-section">
        <div className="reg-container">
          <div className="reg-css-reveal">
            <div className="reg-trust-box">
              <div className="reg-trust-item">
                <FaShieldHalved className="reg-trust-icon" />
                <div>
                  <h4>100% Verified Registration</h4>
                  <p>
                    Instant digital QR code issued directly to your WhatsApp
                    &amp; Email.
                  </p>
                </div>
              </div>

              <div className="reg-trust-divider" />

              <div className="reg-trust-item">
                <FaTicket className="reg-trust-icon" />
                <div>
                  <h4>Yashobhoomi Fast-Track Entry</h4>
                  <p>
                    Skip venue registration queues with pre-approved mobile
                    pass.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegistrationPage;
